import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
// import Cookies from 'js-cookie';
import useUnifiedWebSocket from '../hooks/useUnifiedWebSocket';
import { UserPlus, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import ChatSection from '../components/ChatSection';
import SidebarSection from '../components/SidebarSection';
import ProjectStatusBar from '../components/ProjectStatusBar';
import { useLiveTrack } from '../hooks/useLiveTrack';
import useMessagePins from '../hooks/useMessagePins';
import GoalsModal from '../components/GoalsModal';
import { ParticipantNotificationManager } from '../components/ParticipantNotification';
import { useProjectStore } from '../stores';
import { useAuth } from '../contexts/AuthContext';
import { projectAPI, chatAPI, createQueryKey } from '../services/api';
import { useMobile } from '../hooks/useMobile';
import './ChatPage.css';

const ChatPage = () => {

  // URL params
  const { projectId, chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State for default chat handling
  const [effectiveChatId, setEffectiveChatId] = useState(() => {
    return chatId && chatId !== 'null' && chatId !== 'undefined' ? chatId : null;
  });
  
  const {
    setProjectId
  } = useProjectStore();
  
  const queryClient = useQueryClient();
  const [justJoined, setJustJoined] = useState(false); // Track if user just joined to bypass join screen
  const [shouldRedirect, setShouldRedirect] = useState(false); // SECURITY: Track if we need to redirect due to unauthorized access
  const [intentionExpanded, setIntentionExpanded] = useState(false); // Toggle to show full original intention/prompt
  // legacy ref removed (unified websocket handles connection)
  
  // Debounced query invalidation and refetch to prevent excessive refetches
  const debounceTimeoutRef = useRef(null);
  const debouncedInvalidateQueries = useCallback((queryKey) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(async () => {
      await queryClient.invalidateQueries(queryKey);
      await queryClient.refetchQueries(queryKey);
      debounceTimeoutRef.current = null;
  }, 1000); // Optimized: Increased to 1000ms debounce to reduce excessive invalidations
  }, [queryClient]);
  
  // State for participant join/leave notifications
  const [participantNotifications, setParticipantNotifications] = useState([]);
  // Dedupe recent presence events (join/leave) to avoid duplicates on reconnects
  const recentPresenceEventsRef = useRef(new Map()); // key -> timestamp
  const shouldIgnorePresenceEvent = useCallback((type, userObj) => {
    try {
      const userId = userObj?.id;
      if (!userId) return false;
      // Never notify for current user
      if (user && user.id === userId) return true;
      const key = `${type}:${userId}`;
      const now = Date.now();
      const last = recentPresenceEventsRef.current.get(key) || 0;
      // Ignore if same event seen within the last 10 seconds
      if (now - last < 10000) return true;
      recentPresenceEventsRef.current.set(key, now);
      // Garbage collect old entries sometimes
      if (recentPresenceEventsRef.current.size > 200) {
        const cutoff = now - 60000;
        for (const [k, ts] of recentPresenceEventsRef.current.entries()) {
          if (ts < cutoff) recentPresenceEventsRef.current.delete(k);
        }
      }
      return false;
    } catch (_) { return false; }
  }, [user]);
  
  // Flag to track InteractiveCards interactions to prevent focus management interference
  const isInteractiveCardInteractionRef = useRef(false);
  // Track if panel was expanded due to keyboard focus (inputs)
  const fullByKeyboardRef = useRef(false);
  
  // Reset justJoined flag when projectId changes (e.g., navigating to different project)
  useEffect(() => {
    setJustJoined(false);
  }, [projectId]);

  // Fetch project data for map display
  const { data: projectResponse, isLoading: projectLoading, isFetching: projectFetching } = useQuery({
    queryKey: createQueryKey.project(projectId),
    queryFn: () => projectAPI.getById(projectId),
    enabled: !!projectId && projectId !== 'undefined' && projectId !== 'null',
    retry: false,
    staleTime: 0, // Always fetch fresh data to get latest status
    refetchOnMount: true, // ✅ STALE DATA FIX: Always refetch when revisiting
    refetchOnWindowFocus: true, // ✅ STALE DATA FIX: Refetch when user returns to tab
  });

  const project = projectResponse?.data?.project || projectResponse?.data;
  // Simplified model: projects are always site-only.
  const isSiteProject = true;
  const { data: chatsResponse } = useQuery({
    queryKey: createQueryKey.chats(projectId),
    queryFn: () => chatAPI.getByProject(projectId),
    enabled: !!projectId && (
      (project?.status === 'finished' && !chatId) || 
      isSiteProject || 
      !!chatId // Always fetch if we have a chatId (to check archived status)
    ),
    staleTime: 0,
    refetchOnMount: true,
  });
  const { data: directChatResponse, error: directChatError } = useQuery({
    queryKey: ['chat', effectiveChatId],
    queryFn: () => effectiveChatId ? chatAPI.getChat(effectiveChatId) : null,
    enabled: !!effectiveChatId,
    staleTime: 5000,
    retry: (failureCount, error) => {
      if (error?.response?.status === 403 || error?.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
  const directChat = directChatResponse?.data?.data?.chat;
  const chats = chatsResponse?.data?.data?.chats || [];
  const currentChat = effectiveChatId ? (chats.find(c => c.id === effectiveChatId) || directChat) : null;
  
  let isChatReadOnly = false;
  if (project?.status === 'finished') {
    isChatReadOnly = true;
  } else if (isSiteProject) {
    if (effectiveChatId && !currentChat) {
      isChatReadOnly = true;
    } else if (currentChat) {
      const chatUserId = String(currentChat.userId || '');
      const currentUserId = String(user?.id || '');
      const chatBelongsToUser = chatUserId === currentUserId && chatUserId !== '';
      if (currentChat.isActive === false || currentChat.archivedAt) {
        isChatReadOnly = true;
      } else if (!chatBelongsToUser) {
        isChatReadOnly = true;
      }
    }
  }

  // Track project completion status changes (no victory modal)
  const prevProjectStatusForVictoryRef = useRef(null);
  useEffect(() => {
    if (!project) {
      prevProjectStatusForVictoryRef.current = null;
      return;
    }
    
    const currentStatus = project?.status;
    prevProjectStatusForVictoryRef.current = currentStatus;
  }, [project?.status, project]);
  
  // Live tracking hook when project ongoing
  const liveEnabled = project?.status === 'ongoing';
  const liveTrackingEnabled = !!projectId && !!effectiveChatId && liveEnabled && !isChatReadOnly;
  const { points: liveTrailPoints } = useLiveTrack({
    projectId,
    sessionId: effectiveChatId,
    enabled: liveTrackingEnabled,
    config: { intervalSeconds: 5, minDistanceMeters: 10, maxAccuracyMeters: 100 } // Optimized: Less frequent tracking
  });

  // Track messages from ChatSection for useMessagePins
  const [chatMessages, setChatMessages] = useState([]);

  // Message pins hook for map
  const { clusteredPins: messagePins, shouldShowPins: showMessagePins } = useMessagePins(chatMessages, {
    enabledTypes: ['PHOTO'], // Start with photos only
    clustering: true,
    showInSidebar: !!project && (project.status === 'ongoing' || project.status === 'finished') // Show pins for both ongoing and finished projects
  });

  // Station the guide is currently pointing at: the most recent assistant
  // message with an objective-linked card. Deterministic — the map follows the
  // conversation without any new AI behavior (or hallucination risk).
  const currentObjectiveId = useMemo(() => {
    for (let i = chatMessages.length - 1; i >= 0; i--) {
      const message = chatMessages[i];
      if (message?.role !== 'assistant') continue;
      const cards = message?.message_metadata?.interactive_cards;
      if (!Array.isArray(cards)) continue;
      const linked = cards.find(
        (card) => typeof card?.project_objective_id === 'string' && card.project_objective_id
      );
      if (linked) return linked.project_objective_id;
    }
    return null;
  }, [chatMessages]);

  // SECURITY: Handle unauthorized access - IMMEDIATE redirect, no content should render
  // Check both direct fetch errors AND chat ownership from list BEFORE rendering
  useEffect(() => {
    // CRITICAL: If we have a chatId but no user, wait (don't redirect yet)
    if (!user?.id) return;
    
    // Check if direct fetch returned 403 - IMMEDIATE redirect
    if (directChatError?.response?.status === 403) {
      console.error('[ChatPage] SECURITY: Access denied (403) - redirecting immediately');
      setShouldRedirect(true);
      toast.error('You do not have access to this chat session');
      return;
    }
    
    // Check if chat doesn't exist (404) - redirect to project page
    if (directChatError?.response?.status === 404) {
      console.error('[ChatPage] Chat not found (404) - redirecting to project page');
      setShouldRedirect(true);
      toast.error('Chat session not found');
      return;
    }
    
    // CRITICAL: Check if chat from list belongs to another user - IMMEDIATE redirect
    // This prevents ANY content from rendering for other users' chats
    if (effectiveChatId && chatsResponse?.data?.data?.chats) {
      const chats = chatsResponse.data.data.chats;
      const chatFromList = chats.find(c => c.id === effectiveChatId);
      if (chatFromList && chatFromList.userId) {
        const chatUserId = String(chatFromList.userId);
        const currentUserId = String(user.id);
        if (chatUserId !== currentUserId) {
          console.error('[ChatPage] SECURITY: User attempting to access another user\'s chat - redirecting immediately');
          setShouldRedirect(true);
          toast.error('You can only view your own chat sessions');
          return;
        }
      }
    }
    
    // CRITICAL: Also check directChat if it exists (from direct fetch)
    // This is the most important check - backend returns 403, but we also verify here
    if (directChat && directChat.userId) {
      const chatUserId = String(directChat.userId);
      const currentUserId = String(user.id);
      if (chatUserId !== currentUserId) {
        console.error('[ChatPage] SECURITY: Direct chat fetch returned another user\'s chat - redirecting immediately');
        setShouldRedirect(true);
        toast.error('You can only view your own chat sessions');
        return;
      }
    }
  }, [directChatError, effectiveChatId, chatsResponse?.data?.data?.chats, directChat, user?.id, projectId]);
  
  // SECURITY: Perform redirect in separate effect to avoid React warnings
  useEffect(() => {
    if (shouldRedirect) {
      navigate(`/projects/${projectId}`, { replace: true });
    }
  }, [shouldRedirect, navigate, projectId]);

  // Ensure 1-user-1-chat per project: only resolve chat when necessary
  const prevProjectStatusRef = useRef(project?.status);
  
  useEffect(() => {
    let cancelled = false;
    const resolveChat = async () => {
      if (!projectId) return;
      
      const currentStatus = project?.status;
      const prevStatus = prevProjectStatusRef.current;
      
      // Only call getMineForProject when:
      // 1. Project status changes from non-active to active (pending → ongoing)
      // 2. Current chatId is null/invalid
      const statusChangedToActive = (
        prevStatus && 
        prevStatus !== 'ongoing' && 
        currentStatus === 'ongoing'
      );
      
      // IMPORTANT: If user explicitly navigated to a specific chat (chatId in URL), 
      // respect it and don't override with their own chat. This allows viewing other users' chats.
      const hasExplicitChatId = chatId && chatId !== 'null' && chatId !== 'undefined';
      
      const needsChatResolution = (
        !effectiveChatId || 
        statusChangedToActive
      );
      
      if (!needsChatResolution) {
        console.debug('[chat] skipping chat resolution - no significant change', {
          currentStatus,
          prevStatus,
          effectiveChatId,
          hasExplicitChatId
        });
        return;
      }
      
      console.debug('[chat] resolving chat due to significant change', {
        statusChangedToActive,
        currentStatus,
        prevStatus,
        hasExplicitChatId
      });
      
      // If user explicitly navigated to a chat, respect it (don't override)
      if (hasExplicitChatId && !statusChangedToActive) {
        // User explicitly navigated to a chat - keep it
        setEffectiveChatId(chatId);
        return;
      }
      
      try {
        const res = await chatAPI.getMineForProject(projectId);
        const myChatId = res?.data?.data?.chatId;
        if (!cancelled && myChatId) {
          // Only set to user's own chat if no explicit chatId in URL
          if (!hasExplicitChatId) {
          setEffectiveChatId(myChatId);
          }
        } else if (!cancelled) {
          const newChatId = chatId && chatId !== 'null' && chatId !== 'undefined' ? chatId : null;
          setEffectiveChatId(newChatId);
        }
      } catch (e) {
        if (!cancelled) {
          const newChatId = chatId && chatId !== 'null' && chatId !== 'undefined' ? chatId : null;
          setEffectiveChatId(newChatId);
        }
      }
    };
    
    resolveChat();
    
    // Update refs for next comparison
    prevProjectStatusRef.current = project?.status;
    
    return () => { cancelled = true; };
  }, [projectId, chatId, project?.status, effectiveChatId]);

  // Peer locations state for real-time map updates (other users)
  const [peerLocations, setPeerLocations] = useState({});

  // Shared scrapbook: geotagged photos from ALL participants, as map pins.
  // Seeded from the project's existing photos, grown live via photo_added.
  const [photoPins, setPhotoPins] = useState([]);
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await projectAPI.getImages(projectId, { limit: 100, source: 'user_upload' });
        const images = res?.data?.data?.images || [];
        const pins = images
          .map((img) => {
            const lat = Number(img?.location?.lat);
            const lng = Number(img?.location?.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            return {
              id: img?.metadata?.mediaId || img.url,
              url: img.url,
              lat,
              lng,
              username: img?.takenBy?.firstName || img?.takenBy?.username || null,
              userId: img?.takenBy?.id || null,
              takenAt: img?.takenAt || null,
            };
          })
          .filter(Boolean);
        if (!cancelled && pins.length) {
          setPhotoPins((prev) => {
            const known = new Set(prev.map((p) => p.id));
            return [...prev, ...pins.filter((p) => !known.has(p.id))];
          });
        }
      } catch (_) {
        // Photo pins are decoration — never block the session on them.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Peer markers must not haunt the map: a phone that locked or lost signal
  // stops broadcasting, so prune anything silent for >5 minutes.
  useEffect(() => {
    const PEER_TTL_MS = 5 * 60 * 1000;
    const interval = setInterval(() => {
      setPeerLocations((prev) => {
        if (!prev) return prev;
        const now = Date.now();
        const entries = Object.entries(prev).filter(
          ([, loc]) => now - (loc?.receivedAt || 0) <= PEER_TTL_MS
        );
        if (entries.length === Object.keys(prev).length) return prev;
        return Object.fromEntries(entries);
      });
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Unified WebSocket: subscribe to project channel for events (both single-player and multiplayer)
  const unified = useUnifiedWebSocket(null);
  useEffect(() => {
    if (!projectId) return;
    const projectChannel = `project_${projectId}`;
    const unsubscribe = unified.subscribeToChannel(projectChannel);

    const onProjectFinished = (data) => {
      debouncedInvalidateQueries(createQueryKey.project(projectId));
    };

    const onObjectiveProgress = (data) => {
      // If objective was completed, invalidate immediately (no debounce) for instant UI update
      if (data?.isCompleted) {
        queryClient.invalidateQueries(createQueryKey.project(projectId));
        queryClient.refetchQueries(createQueryKey.project(projectId));
        
        // Notify other participants about the completion
        try {
          const by = data?.confirmedBy;
          const title = data?.objectiveTitle;
          if (by?.id && by.id !== user?.id && title) {
            setParticipantNotifications((prev) => [
              ...prev,
              {
                user: by,
                action: 'objective_fulfilled',
                objectiveTitle: title,
                id: `objective-fulfilled-${Date.now()}-${Math.random()}`,
              },
            ]);
          }
        } catch (_) {}
      } else {
        debouncedInvalidateQueries(createQueryKey.project(projectId));
      }
    };

    const onObjectivesUpdated = (data) => {
      // Notify for batch updates (Plan Updated) if initiated by another user
      if (data?.action === 'batch_update') {
        const updatedBy = data?.updatedBy;
        if (updatedBy && updatedBy.id !== user?.id) {
          setParticipantNotifications(prev => ([...prev, { 
            user: updatedBy, 
            action: 'plan_updated', 
            changesCount: data?.details?.changes_count,
            id: `plan-update-${Date.now()}` 
          }]));
        }
      }

      // Always invalidate immediately for objective updates (status changes are important)
      queryClient.invalidateQueries(createQueryKey.project(projectId));
      queryClient.refetchQueries(createQueryKey.project(projectId));
    };

    // Helper to update participantCount in project cache
    const updateProjectParticipantCount = (newCount) => {
      queryClient.setQueryData(createQueryKey.project(projectId), (oldData) => {
        if (!oldData) return oldData;
        const hasWrappedProject = !!oldData?.data?.project;
        if (hasWrappedProject) {
          return {
            ...oldData,
            data: {
              ...oldData.data,
              project: {
                ...oldData.data.project,
                participantCount: newCount
              }
            }
          };
        } else {
          return {
            ...oldData,
            data: {
              ...oldData.data,
              participantCount: newCount
            }
          };
        }
      });
    };

    const onParticipantJoined = (data) => {
      const u = data?.user;
      if (!u) return;
      if (shouldIgnorePresenceEvent('joined', u)) return;
      
      // Universal handler for all project types:
      // 1. Use authoritative participantCount from backend if available
      // 2. For Site projects: Invalidate chats to ensure "active session" filtering works
      // 3. For Event projects: Update participants cache directly
      
      const isSite = isSiteProject;
      const backendCount = data?.participantCount;
      
      // ALWAYS use backend count if available, fallback to array length
      const newCount = backendCount !== undefined 
        ? backendCount 
        : (Array.isArray(data?.participants) ? data.participants.length : undefined);

      if (newCount !== undefined) {
        updateProjectParticipantCount(newCount);
      }
        
        if (isSite) {
        // Site projects rely on active chats list for participant display
          queryClient.invalidateQueries(createQueryKey.chats(projectId));
        } else {
        // Event projects can update the participants cache directly
        if (Array.isArray(data?.participants)) {
          queryClient.setQueryData(['participants', projectId], { 
            data: { success: true, data: { participants: data.participants } } 
          });
      } else {
          // Fallback if no list provided
          queryClient.invalidateQueries(['participants', projectId]);
        }
      }
      
      // Invalidate active WebSocket users query so ProjectStatusBar and ParticipantsModal update.
      // (invalidateQueries already refetches active queries; explicit refetch doubles requests)
      queryClient.invalidateQueries(['active-websocket-users', projectId]);
      
      debouncedInvalidateQueries(createQueryKey.project(projectId));
      setParticipantNotifications(prev => ([...prev, { user: u, action: 'joined', id: Date.now() }]));
    };

    const onParticipantLeft = (data) => {
      const u = data?.user;
      if (!u) return;
      if (shouldIgnorePresenceEvent('left', u)) return;

      // Remove live map marker for this user (best-effort)
      try {
        const leftUserId = u?.id;
        if (leftUserId) {
          setPeerLocations((prev) => {
            if (!prev || !prev[leftUserId]) return prev;
            const next = { ...prev };
            delete next[leftUserId];
            return next;
          });
        }
      } catch (_) {}
      
      const isSite = isSiteProject;
      const backendCount = data?.participantCount;
      
      // ALWAYS use backend count if available
      const newCount = backendCount !== undefined 
        ? backendCount 
        : (Array.isArray(data?.participants) ? data.participants.length : undefined);

      if (newCount !== undefined) {
        updateProjectParticipantCount(newCount);
      }
        
        if (isSite) {
          queryClient.invalidateQueries(createQueryKey.chats(projectId));
        } else {
        if (Array.isArray(data?.participants)) {
          queryClient.setQueryData(['participants', projectId], { 
            data: { success: true, data: { participants: data.participants } } 
          });
        } else {
          queryClient.invalidateQueries(['participants', projectId]);
        }
      }
      
      // Invalidate active WebSocket users query so ProjectStatusBar and ParticipantsModal update.
      // (invalidateQueries already refetches active queries; explicit refetch doubles requests)
      queryClient.invalidateQueries(['active-websocket-users', projectId]);
      
      debouncedInvalidateQueries(createQueryKey.project(projectId));
      // Ending your session (reason: session_ended) is a finish-line moment,
      // not an exit — celebrate it instead of the alarming "left" toast.
      const leftAction = data?.reason === 'session_ended' ? 'finished' : 'left';
      setParticipantNotifications(prev => ([...prev, { user: u, action: leftAction, id: Date.now() }]));
    };
    const onObjectiveAdded = (data) => {
      const obj = data?.objective;
      const addedBy = obj?.addedBy;
      if (addedBy && addedBy.id !== user?.id) {
        setParticipantNotifications(prev => ([...prev, { user: addedBy, action: 'objective_added', objective: obj, id: `objective-${Date.now()}` }]));
      }
      debouncedInvalidateQueries(createQueryKey.project(projectId));
    };
    const onObjectiveRemoved = (data) => {
      const obj = data?.objective;
      const removedBy = data?.removedBy;
      if (removedBy && removedBy.id !== user?.id) {
        setParticipantNotifications(prev => ([...prev, { user: removedBy, action: 'objective_removed', objective: obj, id: `objective-removed-${Date.now()}` }]));
      }
      debouncedInvalidateQueries(createQueryKey.project(projectId));
    };
    const onShout = (data) => {
      if (data?.user && data?.message) {
        setParticipantNotifications(prev => ([...prev, { user: data.user, action: 'shout', shoutMessage: data.message, id: `shout-${Date.now()}-${Math.random()}` }]));
      }
    };

    // A peer's websocket just came online on this project. participant_joined
    // fires at redemption time — BEFORE the joiner's page has a socket — so
    // the multiplayer chrome (gated on active-chat ∩ active-websocket) would
    // otherwise only appear on the 60s fallback poll. No toast: the join
    // toast already happened; this is silent cache refresh only.
    const onPresenceUpdate = (data) => {
      if (!data?.userId || data.userId === user?.id) return;
      queryClient.invalidateQueries(['active-websocket-users', projectId]);
      if (isSiteProject) {
        queryClient.invalidateQueries(createQueryKey.chats(projectId));
      }
    };

    // Live location updates (from Track ingestion broadcasts)
    const onUserLocationUpdate = (data) => {
      const uid = data?.userId;
      const loc = data?.location;
      if (!uid || !loc) return;
      // Ignore self: our own location is rendered via useLiveTrack()
      if (uid === user?.id) return;
      const lat = Number(loc.lat);
      const lng = Number(loc.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      setPeerLocations((prev) => ({
        ...(prev || {}),
        [uid]: {
          lat,
          lng,
          ts: loc.ts || null,
          accuracy: loc.accuracy,
          heading: loc.heading,
          speed: loc.speed,
          username: data?.username || null,
          avatar: data?.avatar || null,
          // Local clock, for staleness pruning (device ts can't be trusted)
          receivedAt: Date.now(),
        },
      }));
    };

    // Shared scrapbook: a companion's geotagged photo becomes a pin on
    // everyone's map (their chat context stays private).
    const onPhotoAdded = (data) => {
      const loc = data?.location;
      if (!data?.mediaId || !loc) return;
      const lat = Number(loc.lat);
      const lng = Number(loc.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      setPhotoPins((prev) => {
        if (prev.some((p) => p.id === data.mediaId)) return prev;
        return [
          ...prev,
          {
            id: data.mediaId,
            url: data.url,
            lat,
            lng,
            username: data.username || null,
            userId: data.userId || null,
            takenAt: data.takenAt || null,
          },
        ];
      });
    };

    const off1 = unified.registerHandler('project_finished', onProjectFinished, projectChannel);
    const off2 = unified.registerHandler('objective_progress', onObjectiveProgress, projectChannel);
    const offObjectivesUpdated = unified.registerHandler('project_objectives_updated', onObjectivesUpdated, projectChannel);
    
    const off3 = unified.registerHandler('participant_joined', onParticipantJoined, projectChannel);
    const off4 = unified.registerHandler('participant_left', onParticipantLeft, projectChannel);
    const off5 = unified.registerHandler('objective_added', onObjectiveAdded, projectChannel);
    const off6 = unified.registerHandler('objective_removed', onObjectiveRemoved, projectChannel);
    const off7 = unified.registerHandler('shout', onShout, projectChannel);
    const off8 = unified.registerHandler('user_location_update', onUserLocationUpdate, projectChannel);
    const off9 = unified.registerHandler('presence_update', onPresenceUpdate, projectChannel);
    const off10 = unified.registerHandler('photo_added', onPhotoAdded, projectChannel);

    return () => {
      try { off1 && off1(); off2 && off2(); offObjectivesUpdated && offObjectivesUpdated(); } catch (_) {}
      try { off3 && off3(); off4 && off4(); off5 && off5(); off6 && off6(); off7 && off7(); off8 && off8(); off9 && off9(); off10 && off10(); } catch (_) {}
      try { unsubscribe && unsubscribe(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isSiteProject, user?.id, queryClient, shouldIgnorePresenceEvent]);

  // End session (archive current chat)
  const finishProjectMutation = useMutation({
    mutationFn: () => {
      if (!project) return Promise.reject('No project found');
      return chatAPI.archiveCurrentSession(projectId);
    },
    onSuccess: async () => {
      if (isSiteProject) {
        // Immediately update the cache to mark the active chat as archived
        // This provides instant UI feedback before refetch
        queryClient.setQueryData(['project-chats', projectId], (oldData) => {
          if (!oldData?.data?.data?.chats) return oldData;
          
          const updatedChats = oldData.data.data.chats.map(chat => {
            // Mark the active chat as archived immediately
            if (chat.isActive === true && chat.userId === user?.id) {
              return {
                ...chat,
                isActive: false,
                archivedAt: new Date().toISOString()
              };
            }
            return chat;
          });
          
          return {
            ...oldData,
            data: {
              ...oldData.data,
              data: {
                ...oldData.data.data,
                chats: updatedChats
              }
            }
          };
        });
        
        // Invalidate all related queries
        queryClient.invalidateQueries(['project-chats', projectId]);
        queryClient.invalidateQueries(['chats', projectId]);
        queryClient.invalidateQueries(createQueryKey.chats(projectId));
        queryClient.invalidateQueries(createQueryKey.project(projectId));
        queryClient.invalidateQueries(['user-projects']);
        // Invalidate NewProjectPage queries to refresh /new page
        queryClient.invalidateQueries({ queryKey: ['new-page-live-projects'] });
        queryClient.invalidateQueries({ queryKey: ['new-page-ongoing-projects'] });
        
        // Force refetch the project so backend can correct status from ongoing -> pending
        // This ensures the project status is updated immediately after archiving session
        try {
          await queryClient.refetchQueries({
            queryKey: createQueryKey.project(projectId),
            type: 'all'
          });
          // Also refetch user-projects to get updated status
          await queryClient.refetchQueries({
            queryKey: ['user-projects'],
            type: 'all'
          });
          // Refetch NewProjectPage queries to get updated list
          await queryClient.refetchQueries({
            queryKey: ['new-page-live-projects'],
            type: 'all'
          });
          await queryClient.refetchQueries({
            queryKey: ['new-page-ongoing-projects'],
            type: 'all'
          });
        } catch (err) {
          console.warn('Refetch error (non-critical):', err);
        }
        
        // Navigate back to dashboard
        // Include the chatId so the review modal can link the review to this session
        setTimeout(() => {
          navigate(`/projects/${projectId}`, { 
            state: { 
              sessionEnded: true, 
              endedChatId: effectiveChatId || null
            } 
          });
        }, 200);
        return;
      }
    },
    onError: (error) => {
      console.error('Failed to finish project:', error);
      if (isSiteProject) {
        toast.error('Failed to end session');
      } else {
        toast.error('Failed to finish project');
      }
    }
  });

  const handleFinishProject = () => {
    setShowFinishConfirmation(false);
    finishProjectMutation.mutate();
  };

  const handleCancelFinish = () => {
    setShowFinishConfirmation(false);
  };

  const handleGoToProjectSettings = () => {
    setShowFinishConfirmation(false);
    navigate(`/projects/${projectId}?overview=true`);
  };

  // Handle finish button click - skip modal for archived/read-only sessions
  const handleFinishButtonClick = useCallback((opts = {}) => {
    if (isChatReadOnly) {
      // For archived/read-only sessions, go directly to project settings
      navigate(`/projects/${projectId}?overview=true`);
    } else if (opts?.skipConfirmation) {
      // Wrap-up decision card: the user just said "I'm done" — a second
      // "are you sure?" would be friction at the conversion moment.
      finishProjectMutation.mutate();
    } else {
      // For active sessions, show confirmation modal
      setShowFinishConfirmation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChatReadOnly, navigate, projectId]);

  // Calculate map zoom target to fit user location and all pins
  const calculateMapZoomTarget = useCallback((suggestions) => {
    if (!suggestions || suggestions.length === 0) {
      return null;
    }

    // Get user location from trail points if available
    let userCoords = null;
    if (liveTrailPoints && liveTrailPoints.length > 0) {
      const lastPoint = liveTrailPoints[liveTrailPoints.length - 1];
      userCoords = { lat: lastPoint[0], lng: lastPoint[1] };
    }

    const allCoords = [
      ...(userCoords ? [userCoords] : []), // User location if available
      ...suggestions.map(s => s.coordinates) // All pin locations
    ];

    if (allCoords.length === 0) {
      return null;
    }

    const lats = allCoords.map(c => c.lat);
    const lngs = allCoords.map(c => c.lng);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    // Calculate center point
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    // Calculate zoom level based on bounds
    // Approximate zoom calculation based on latitude span
    const latSpan = maxLat - minLat;
    const lngSpan = maxLng - minLng;
    const maxSpan = Math.max(latSpan, lngSpan);
    
    // Convert span to approximate zoom level (empirical formula)
    // Adjusted for better zoom levels - closer in for better detail
    let zoom = 16; // Default zoom
    if (maxSpan > 0.1) zoom = 12;      // Very wide area
    else if (maxSpan > 0.05) zoom = 13; // Wide area
    else if (maxSpan > 0.02) zoom = 14; // Medium area
    else if (maxSpan > 0.01) zoom = 15; // Small area
    else if (maxSpan > 0.005) zoom = 16; // Very small area
    else zoom = 17; // Tiny area

    return {
      lat: centerLat,
      lng: centerLng,
      zoom: zoom
    };
  }, [liveTrailPoints]);

  // Mobile responsive state
  const { isMobile } = useMobile();
  const TINY_HEIGHT = 22;
  const MEDIUM_HEIGHT = 58;
  const FULL_HEIGHT = 94; // Used for comparison only; actual height uses calc() for reliability
  const SNAP_CHANGE_THRESHOLD = 8;
  const STATUS_BAR_HEIGHT_PX = 52; // Height of ProjectStatusBar (h-11 button + py-1 padding + margin)
  const hasMobileStatusBar = !isChatReadOnly && !!project && (project.status !== 'finished' || isSiteProject);
  const fullHeightChatPanelCss = hasMobileStatusBar
    ? `calc(100dvh - var(--app-safe-area-top) - ${STATUS_BAR_HEIGHT_PX}px)`
    : '100dvh';
  const [chatPanelHeight, setChatPanelHeight] = useState(MEDIUM_HEIGHT); // Start at Medium


  // Handle AI suggestions from chat
  // AI suggestions: temp pins from location cards
  const handleAISuggestionsChange = useCallback((suggestions) => {
    setAISuggestions(suggestions || []);
    
    // On mobile: when expanding a location card (multiple suggestions), if sheet is Full → set to Medium for map context
    try {
      if (isMobile && Array.isArray(suggestions) && suggestions.length > 1 && chatPanelHeight === FULL_HEIGHT) {
        setChatPanelHeight(MEDIUM_HEIGHT);
      }
    } catch (_) {}
    
    // Calculate and set map zoom target to show user + all pins
    if (suggestions && suggestions.length > 0) {
      const zoomTarget = calculateMapZoomTarget(suggestions);
      if (zoomTarget) {
        // console.log('🗺️ Setting map zoom target to fit user + pins:', zoomTarget);
        setMapZoomTarget(zoomTarget);
      }
    }
  }, [calculateMapZoomTarget, isMobile, chatPanelHeight, FULL_HEIGHT, MEDIUM_HEIGHT]);

  // Persisted AI pin (single), separate from temp suggestions
  const [persistedAiPins, setPersistedAiPins] = useState([]);
  const [mapZoomTarget, setMapZoomTarget] = useState(null);
  
  const handlePersistLocationPin = useCallback((pin) => {
    console.log('📍 ChatPage: handlePersistLocationPin called with:', pin);
    
    // If pin is null, clear all persisted pins
    if (!pin) {
      console.log('📍 ChatPage: Clearing all persisted pins');
      setPersistedAiPins([]);
      return;
    }
    
    if (!pin.coordinates) {
      console.warn('📍 ChatPage: Pin has no coordinates, ignoring');
      return;
    }
    
    // On mobile: selecting a specific location (persisted single pin) → minimize sheet to Tiny
    try {
      if (isMobile) {
        setChatPanelHeight(TINY_HEIGHT);
      }
    } catch (_) {}
    
    console.log('📍 ChatPage: Clearing temp suggestions and persisting single pin');
    setAISuggestions([]); // clear temp pins
    setPersistedAiPins([{ ...pin, source: 'ai-persist' }]);
    
    // Calculate zoom target to show both user location and the persisted pin
    const suggestions = [{ coordinates: pin.coordinates }];
    const zoomTarget = calculateMapZoomTarget(suggestions);
    if (zoomTarget) {
      console.log('📍 ChatPage: Setting zoom target for persisted pin:', zoomTarget);
      setMapZoomTarget(zoomTarget);
    } else {
      // Fallback to fixed zoom if calculation fails
      const fallbackTarget = {
        lat: pin.coordinates.lat,
        lng: pin.coordinates.lng,
        zoom: 14
      };
      console.log('📍 ChatPage: Using fallback zoom target:', fallbackTarget);
      setMapZoomTarget(fallbackTarget);
    }
    
    // Clear zoom target after a moment to allow re-zoom to same location
    setTimeout(() => {
      console.log('📍 ChatPage: Clearing zoom target');
      setMapZoomTarget(null);
    }, 1000);
  }, [calculateMapZoomTarget, isMobile, TINY_HEIGHT]);

  // Map-first station handoff: a chat chip asks the map to focus a station pin
  // (and open its popup, which carries the Route link). On mobile the chat
  // sheet drops to half height so the map is actually visible.
  const [stationFocusRequest, setStationFocusRequest] = useState(null);
  const handleShowStationOnMap = useCallback((objectiveId) => {
    if (!objectiveId) return;
    if (isMobile) {
      setChatPanelHeight(MEDIUM_HEIGHT);
    }
    setStationFocusRequest({ id: objectiveId, nonce: Date.now() });
  }, [isMobile, MEDIUM_HEIGHT]);

  // Reset per-user map state when active chat changes (isolate maps in multiplayer)
  // Only clear when chatId actually changes to a different chat, not when it's set for the first time
  const prevEffectiveChatIdRef = useRef(effectiveChatId);
  useEffect(() => {
    const prevChatId = prevEffectiveChatIdRef.current;
    const currentChatId = effectiveChatId;
    
    // Only clear AI suggestions if we're switching between different chats
    // Don't clear when setting chatId for the first time (null → chatId)
    if (prevChatId !== null && prevChatId !== currentChatId && currentChatId !== null) {
      setAISuggestions([]);
      setPersistedAiPins([]);
      setMapZoomTarget(null);
    }
    
    prevEffectiveChatIdRef.current = currentChatId;
  }, [effectiveChatId]);


  // (moved mobile responsive state above to avoid use-before-define in hooks)
  const [isDragging, setIsDragging] = useState(false);
  const previousHeightRef = useRef(MEDIUM_HEIGHT);
  const isDraggingRef = useRef(false);
  const didMoveRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(MEDIUM_HEIGHT);
  const dragCurrentHeightRef = useRef(MEDIUM_HEIGHT);
  const dragAnimationFrameRef = useRef(null);
  // Track if user has manually interacted with chat panel height (to avoid overriding their preference)
  const hasManuallyChangedHeightRef = useRef(false);
  
  // Confirmation modal state
  const [showFinishConfirmation, setShowFinishConfirmation] = useState(false);
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [celebrateGoals, setCelebrateGoals] = useState(false);
  
  // Note: We no longer use "pause" for Events - they finish when done
  // Sites use "End Session" (archive) instead of pause
  // All logic now uses isSiteProject instead of isEndlessProject
  
  // AI suggestions state for map integration
  const [aiSuggestions, setAISuggestions] = useState([]);

  // Handle escape key for modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showFinishConfirmation) {
        setShowFinishConfirmation(false);
      }
    };

    if (showFinishConfirmation) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showFinishConfirmation]);
  
  // Refs
  const containerRef = useRef(null);
  const chatPanelRef = useRef(null);
  const dragHandleRef = useRef(null);
  const mapContainerRef = useRef(null);

  const applyDraggedPanelHeight = useCallback((height) => {
    dragCurrentHeightRef.current = height;

    if (dragAnimationFrameRef.current) return;

    dragAnimationFrameRef.current = requestAnimationFrame(() => {
      dragAnimationFrameRef.current = null;

      if (!chatPanelRef.current) return;

      chatPanelRef.current.style.height =
        dragCurrentHeightRef.current >= FULL_HEIGHT
          ? fullHeightChatPanelCss
          : `${dragCurrentHeightRef.current}%`;
    });
  }, [FULL_HEIGHT, fullHeightChatPanelCss]);

  useEffect(() => {
    return () => {
      if (dragAnimationFrameRef.current) {
        cancelAnimationFrame(dragAnimationFrameRef.current);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      // Reset chat panel height on orientation change
      if (isMobile) {
        setChatPanelHeight(MEDIUM_HEIGHT);
      }
    };
    
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [isMobile]);

  useEffect(() => {
    if (projectId) {
      setProjectId(projectId);
    }
  }, [projectId, setProjectId]);

  // Add mobile-page class to body when on mobile to prevent scrolling
  useEffect(() => {
    if (isMobile) {
      document.body.classList.add('mobile-page');
      return () => {
        document.body.classList.remove('mobile-page');
      };
    }
  }, [isMobile]);

  // Auto-expand chat panel on mobile after a brief delay for better readability
  // This gives users time to see the map, then expands so they can read agent messages
  useEffect(() => {
    if (!isMobile) return;
    
    const timer = setTimeout(() => {
      // Only auto-expand if user hasn't manually changed the height
      if (!hasManuallyChangedHeightRef.current) {
        setChatPanelHeight(FULL_HEIGHT);
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [isMobile, FULL_HEIGHT]);

  // ✅ STALE DATA FIX: Refetch data when user returns to tab for ongoing projects
  useEffect(() => {
    if (!project || project.status !== 'ongoing') return;

    const handleVisibilityChange = () => {
      if (!document.hidden && project?.status === 'ongoing') {
        console.debug('[ChatPage] User returned to tab, refetching data for ongoing project');
        // Refetch project data
        queryClient.invalidateQueries(createQueryKey.project(projectId));
        // Refetch chat data if we have a chatId
        if (effectiveChatId) {
          queryClient.invalidateQueries(['chat', effectiveChatId]);
        }
        // Refetch participants
        queryClient.invalidateQueries(['participants', projectId]);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [project, projectId, effectiveChatId, queryClient]);

  // Helpers for snapping and cycling
  const getNearestSnap = useCallback((height) => {
    const targets = [TINY_HEIGHT, MEDIUM_HEIGHT, FULL_HEIGHT];
    let nearest = targets[0];
    let minDiff = Math.abs(height - targets[0]);
    for (let i = 1; i < targets.length; i++) {
      const diff = Math.abs(height - targets[i]);
      if (diff < minDiff) {
        minDiff = diff;
        nearest = targets[i];
      }
    }
    return nearest;
  }, [TINY_HEIGHT, MEDIUM_HEIGHT, FULL_HEIGHT]);

  const cycleSnap = useCallback((current) => {
    const nearest = getNearestSnap(current);
    if (nearest === TINY_HEIGHT) return MEDIUM_HEIGHT;
    if (nearest === MEDIUM_HEIGHT) return FULL_HEIGHT;
    return TINY_HEIGHT;
  }, [getNearestSnap, TINY_HEIGHT, MEDIUM_HEIGHT, FULL_HEIGHT]);

  const getDirectionalSnap = useCallback((startHeight, endHeight) => {
    const startSnap = getNearestSnap(startHeight);
    const delta = endHeight - startHeight;

    if (Math.abs(delta) < SNAP_CHANGE_THRESHOLD) {
      return getNearestSnap(endHeight);
    }

    if (startSnap === TINY_HEIGHT) {
      return delta > 0 ? MEDIUM_HEIGHT : TINY_HEIGHT;
    }

    if (startSnap === MEDIUM_HEIGHT) {
      if (delta > 0) return FULL_HEIGHT;
      if (delta < 0) return TINY_HEIGHT;
    }

    if (startSnap === FULL_HEIGHT) {
      return delta < 0 ? MEDIUM_HEIGHT : FULL_HEIGHT;
    }

    return getNearestSnap(endHeight);
  }, [
    getNearestSnap,
    SNAP_CHANGE_THRESHOLD,
    TINY_HEIGHT,
    MEDIUM_HEIGHT,
    FULL_HEIGHT,
  ]);

  // Drag handlers
  const handleDragStart = useCallback((clientY) => {
    if (isDraggingRef.current) return;

    isDraggingRef.current = true;
    setIsDragging(true);
    dragStartYRef.current = clientY;
    dragStartHeightRef.current = chatPanelHeight;
    dragCurrentHeightRef.current = chatPanelHeight;
    didMoveRef.current = false;
    
    // Prevent text selection during drag
    document.body.style.userSelect = 'none';
    document.body.classList.add('mobile-dragging');
    attachGlobalDragListeners();
  }, [chatPanelHeight]);

  const handleDragMove = useCallback((clientY) => {
    if (!isDraggingRef.current || !containerRef.current) return;
    
    const containerHeight = containerRef.current.offsetHeight;
    const deltaY = dragStartYRef.current - clientY; // Inverted: drag up = positive
    const deltaPercent = (deltaY / containerHeight) * 100;
    
    let newHeight = dragStartHeightRef.current + deltaPercent;
    
    // Constrain between 22% and 100%
    newHeight = Math.max(TINY_HEIGHT, Math.min(100, newHeight));
    if (Math.abs(deltaPercent) > 1) {
      didMoveRef.current = true;
    }
    
    applyDraggedPanelHeight(newHeight);
  }, [TINY_HEIGHT, applyDraggedPanelHeight]);

  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    
    isDraggingRef.current = false;
    setIsDragging(false);
    removeGlobalDragListeners();
    document.body.style.userSelect = '';
    document.body.classList.remove('mobile-dragging');
    
    // Mark that user has manually interacted with the panel
    hasManuallyChangedHeightRef.current = true;

    if (dragAnimationFrameRef.current) {
      cancelAnimationFrame(dragAnimationFrameRef.current);
      dragAnimationFrameRef.current = null;
    }

    const finalHeight = dragCurrentHeightRef.current;
    
    // Tap-to-cycle if there was no meaningful movement
    if (!didMoveRef.current || Math.abs(finalHeight - dragStartHeightRef.current) < 3) {
      setChatPanelHeight(cycleSnap(finalHeight));
      return;
    }

    // Bias toward the drag direction so moving between snaps feels easier on touch devices.
    const targetHeight = getDirectionalSnap(dragStartHeightRef.current, finalHeight);
    setChatPanelHeight(targetHeight);
  }, [cycleSnap, getDirectionalSnap]);

  // Keyboard rule: focus inside panel → Full, blur → restore previous
  useEffect(() => {
    if (!isMobile || !chatPanelRef.current) return;
    const panel = chatPanelRef.current;
    const onFocusIn = (e) => {
      const target = e.target;
      if (!target) return;
      const isEditable = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (isEditable) {
        previousHeightRef.current = chatPanelHeight;
        setChatPanelHeight(FULL_HEIGHT);
        fullByKeyboardRef.current = true;
      }
    };
    const onFocusOut = () => {
      // Delay to allow focus to move within inputs
      setTimeout(() => {
        const ae = document.activeElement;
        const stillEditing = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
        
        // Only collapse if: not editing, not interacting with cards, and panel was expanded by keyboard focus
        if (!stillEditing && !isInteractiveCardInteractionRef.current && fullByKeyboardRef.current) {
          setChatPanelHeight(previousHeightRef.current ?? MEDIUM_HEIGHT);
          fullByKeyboardRef.current = false;
        }
      }, 100);
    };
    panel.addEventListener('focusin', onFocusIn);
    panel.addEventListener('focusout', onFocusOut);
    return () => {
      panel.removeEventListener('focusin', onFocusIn);
      panel.removeEventListener('focusout', onFocusOut);
    };
  }, [isMobile, chatPanelHeight, FULL_HEIGHT, MEDIUM_HEIGHT]);

  // Auto-minimize from Medium to Tiny only on map interactions (pan/zoom), not on top controls
  useEffect(() => {
    if (!isMobile || !mapContainerRef.current) return;
    let tracking = null;

    const cleanup = () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerEnd);
      document.removeEventListener('pointercancel', onPointerEnd);
      tracking = null;
    };

    const onPointerMove = (e) => {
      if (!tracking) return;
      const dx = (e.clientX || 0) - tracking.startX;
      const dy = (e.clientY || 0) - tracking.startY;
      if (dx * dx + dy * dy > 64) { // >8px movement => pan gesture
        if (chatPanelHeight === MEDIUM_HEIGHT) {
          setChatPanelHeight(TINY_HEIGHT);
        }
        cleanup();
      }
    };

    const onPointerEnd = () => cleanup();

    const onPointerDown = (e) => {
      if (showFinishConfirmation) return;
      if (chatPanelHeight !== MEDIUM_HEIGHT) return;
      if (!mapContainerRef.current) return;
      const inMap = mapContainerRef.current.contains(e.target);
      const inSheet = chatPanelRef.current && chatPanelRef.current.contains(e.target);
      if (inMap && !inSheet) {
        tracking = { startX: e.clientX || 0, startY: e.clientY || 0 };
        document.addEventListener('pointermove', onPointerMove, { passive: true });
        document.addEventListener('pointerup', onPointerEnd, { passive: true });
        document.addEventListener('pointercancel', onPointerEnd, { passive: true });
      }
    };

    const onWheel = (e) => {
      if (!mapContainerRef.current) return;
      const inMap = mapContainerRef.current.contains(e.target);
      if (inMap && chatPanelHeight === MEDIUM_HEIGHT) {
        setChatPanelHeight(TINY_HEIGHT);
      }
    };

    const mapEl = mapContainerRef.current;
    mapEl.addEventListener('pointerdown', onPointerDown, { passive: true });
    mapEl.addEventListener('wheel', onWheel, { passive: true });
    return () => {
      mapEl.removeEventListener('pointerdown', onPointerDown);
      mapEl.removeEventListener('wheel', onWheel);
      cleanup();
    };
  }, [isMobile, chatPanelHeight, MEDIUM_HEIGHT, TINY_HEIGHT, showFinishConfirmation]);

  // Mouse events
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    handleDragStart(e.clientY);
  }, [handleDragStart]);

  const handleMouseMove = useCallback((e) => {
    handleDragMove(e.clientY);
  }, [handleDragMove]);

  const handleMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Touch events - improved for mobile
  const handleTouchStart = useCallback((e) => {
    // Only handle single touch
    if (e.touches.length !== 1) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    handleDragStart(touch.clientY);
  }, [handleDragStart]);

  const handleTouchMove = useCallback((e) => {
    if (!isDraggingRef.current || e.touches.length !== 1) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    handleDragMove(touch.clientY);
  }, [handleDragMove]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    handleDragEnd();
  }, [handleDragEnd]);

  function attachGlobalDragListeners() {
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
    document.addEventListener('touchcancel', handleTouchEnd, { passive: false });
  }

  function removeGlobalDragListeners() {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
    document.removeEventListener('touchcancel', handleTouchEnd);
  }


  const showSidebar = true;
  const showChat = true;

  // Fetch participants list
  const { data: participantsResponse } = useQuery({
    queryKey: ['participants', projectId],
    queryFn: () => projectAPI.getParticipants(projectId),
    enabled: !!projectId,
    staleTime: 5000,
  });

  // Check if user is a participant (owner or in participants list)
  const isOwner = user && project && user.id === project.ownerId;
  
  // For Site projects: filter participants to only show users with active chats (currently in session)
  const allParticipants = useMemo(() => 
    participantsResponse?.data?.data?.participants || participantsResponse?.data?.participants || []
  , [participantsResponse?.data?.data?.participants, participantsResponse?.data?.participants]);
  
  const participants = useMemo(() => {
    if (!isSiteProject) return allParticipants;
    // Site projects: filter to only users with active chats (currently in session)
    const chats = chatsResponse?.data?.data?.chats || [];
    const activeChatUserIds = new Set(
      chats
        .filter(c => c.isActive === true && !c.archivedAt)
        .map(c => c.userId)
        .filter(Boolean)
    );
    
    return allParticipants.filter(p => activeChatUserIds.has(p.id));
  }, [isSiteProject, allParticipants, chatsResponse?.data?.data?.chats]);
  
  const isParticipant = isOwner || allParticipants.some(p => p.id === user?.id);

  // Memoize participant elements to prevent expensive re-renders
  // Join project mutation
  const joinProjectMutation = useMutation({
    mutationFn: () => projectAPI.join(projectId),
    onSuccess: async () => {
      console.log('✅ Successfully joined project, updating state...');
      // toast.success('Joined project! Loading your chat...');
      // Set flag to bypass join screen
      setJustJoined(true);
      // Invalidate and refetch to get updated data
      await queryClient.invalidateQueries(createQueryKey.project(projectId));
      await queryClient.invalidateQueries(['participants', projectId]);
      await queryClient.refetchQueries(['participants', projectId]);
      console.log('✅ Queries refetched, chat should now load');
      // Immediately bind to my chat to isolate map state
      try {
        const res = await chatAPI.getMineForProject(projectId);
        const myChatId = res?.data?.data?.chatId;
        if (myChatId) {
          setEffectiveChatId(myChatId);
        }
      } catch (_) {}
    },
    onError: (error) => {
      console.error('Failed to join project:', error);
      toast.error(error?.response?.data?.detail || 'Failed to join project');
    }
  });

  // Show "Join Project" screen for ongoing projects when user is not a participant
  // (unless they just joined, in which case we bypass and show the chat)
  if (
    project?.status === 'ongoing' &&
    !isParticipant &&
    !justJoined // Bypass join screen after successful join
  ) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        {/* Subtle background pattern */}
        <div className="fixed inset-0 opacity-30 dark:opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(34, 197, 94, 0.08) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.06) 0%, transparent 50%)`
        }} />
        
        <div className="max-w-md w-full relative z-10">
          {/* Back button - floating */}
          <button
            onClick={() => navigate('/starters')}
            className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {/* Main card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
            {/* Live indicator bar */}
            <div className="bg-emerald-500 px-4 py-2 flex items-center justify-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <span className="text-sm font-medium text-white">Live Now</span>
            </div>
            
            {/* Content */}
            <div className="p-6">
              {/* Project name with expandable original intention */}
              <div className="mb-1">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {project.name}
                </h1>
                {/* Show expand button if original intention is longer than displayed name */}
                {project.settings?.quickStartPrompt && 
                 project.settings.quickStartPrompt.length > (project.name?.length || 0) && (
                  <button
                    onClick={() => setIntentionExpanded(!intentionExpanded)}
                    className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  >
                    {intentionExpanded ? (
                      <>
                        <ChevronUp className="w-3 h-3" />
                        <span>Hide original prompt</span>
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-3 h-3" />
                        <span>Show original prompt</span>
                      </>
                    )}
                  </button>
                )}
                {/* Expanded original intention */}
                {intentionExpanded && project.settings?.quickStartPrompt && (
                  <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                      "{project.settings.quickStartPrompt}"
                    </p>
                  </div>
                )}
              </div>
              
              {/* Host info - compact */}
          {project.owner && (
                <div className="flex items-center gap-2 mb-4 text-sm text-gray-500 dark:text-gray-400">
                  <span>Hosted by</span>
                  <div className="flex items-center gap-1.5">
                {project.owner.avatar ? (
                  <img 
                            src={project.owner.avatar.startsWith('/') ? project.owner.avatar : require('../config/runtime').getImageUrl(project.owner.avatar)} 
                    alt="" 
                        className="w-5 h-5 rounded-full object-cover" 
                  />
                ) : (
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-400 to-blue-400 flex items-center justify-center text-white text-[10px] font-bold">
                        {(project.owner.username)?.[0]?.toUpperCase() || '?'}
              </div>
                    )}
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {project.owner.firstName || project.owner.username}
                    </span>
              </div>
            </div>
          )}

              {/* Description - if present (only show if different from quickStartPrompt) */}
              {project.description && project.description !== project.settings?.quickStartPrompt && (
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-5 line-clamp-2">
                  {project.description}
                </p>
              )}

              {/* Participants - compact avatar stack */}
          {participants.length > 0 && (
                <div className="flex items-center justify-between mb-6 py-3 px-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    {/* Avatar stack */}
                    <div className="flex -space-x-2">
                      {participants.slice(0, 4).map((p, idx) => (
                        <div 
                          key={p.id} 
                          className="relative"
                          style={{ zIndex: 4 - idx }}
                        >
                          {p.avatar ? (
                            <img 
                              src={p.avatar.startsWith('/') ? p.avatar : require('../config/runtime').getImageUrl(p.avatar)}
                              alt=""
                              className="w-8 h-8 rounded-full object-cover border-2 border-white dark:border-gray-800"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white text-xs font-bold border-2 border-white dark:border-gray-800">
                              {(p.username || p.email)?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                      ))}
                {participants.length > 4 && (
                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300 border-2 border-white dark:border-gray-800">
                          +{participants.length - 4}
                        </div>
                )}
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {participants.length} {participants.length === 1 ? 'person' : 'people'} active
                    </span>
              </div>
            </div>
          )}

              {/* Join button */}
            <button
              onClick={() => joinProjectMutation.mutate()}
              disabled={joinProjectMutation.isLoading}
                className="w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {joinProjectMutation.isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Joining...
                  </span>
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                    Join Now
                </>
              )}
            </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Avoid UI flicker: wait for project to load before rendering main layout
  if (!project && (projectLoading || projectFetching)) {
    return (
      <div className="flex h-full bg-white dark:bg-gray-900" />
    );
  }

  // No special finished-project routing; chats remain viewable.

  // CRITICAL SECURITY CHECK: If we should redirect, don't render anything
  if (shouldRedirect) {
    return null; // Return null to prevent any content from rendering
  }
  
  // CRITICAL SECURITY CHECK: If chat fetch returned 404, don't render
  if (effectiveChatId && directChatError?.response?.status === 404) {
    return null; // Redirect is being handled by useEffect
  }
  
  // CRITICAL SECURITY CHECK: If we have a chatId but ownership verification is still loading, don't render
  // This prevents content from flashing before we verify ownership
  if (effectiveChatId && user?.id && !directChatError && !directChatResponse) {
    // Still loading ownership verification - wait before rendering
    return null;
  }
  
  // CRITICAL SECURITY CHECK: If chat exists and doesn't belong to user, don't render
  // The useEffect above will set shouldRedirect, but we also check here to prevent flash
  if (effectiveChatId && user?.id && currentChat && currentChat.userId) {
    const chatUserId = String(currentChat.userId);
    const currentUserId = String(user.id);
    if (chatUserId !== currentUserId) {
      // Don't render - redirect is being handled by useEffect
      return null;
    }
  }
  // Debug logging with detailed comparison
  // const chatFromList = chats.find(c => c.id === effectiveChatId);
  // const chatBelongsToUser = currentChat ? (String(currentChat.userId) === String(user?.id)) : false;
  // console.log('[ChatPage] Chat read-only check:', {
  //   chatId: effectiveChatId,
  //   currentChat: currentChat ? { 
  //     id: currentChat.id, 
  //     userId: currentChat.userId,
  //     userIdType: typeof currentChat.userId,
  //     currentUserId: user?.id,
  //     currentUserIdType: typeof user?.id,
  //     isActive: currentChat.isActive, 
  //     archivedAt: currentChat.archivedAt,
  //     belongsToUser: chatBelongsToUser,
  //     source: chatFromList ? 'list' : (directChat ? 'direct' : 'none')
  //   } : 'NOT FOUND',
  //   isChatReadOnly,
  //   willRenderInput: !!(effectiveChatId && !isChatReadOnly),
  //   chatsCount: chats.length,
  //   chatsResponseLoaded: !!chatsResponse,
  //   directChatLoaded: !!directChatResponse,
  //   chatFromList: chatFromList ? { userId: chatFromList.userId, userIdType: typeof chatFromList.userId } : null,
  //   directChat: directChat ? { userId: directChat.userId, userIdType: typeof directChat.userId } : null
  // });

  // Single ChatSection instance to prevent duplicate queries
  const chatSectionElement = (
    <ChatSection 
      projectId={projectId}
      chatId={effectiveChatId}
      setActiveChatId={setEffectiveChatId}
      project={project}
      isReadOnly={isChatReadOnly}
      onAISuggestionsChange={handleAISuggestionsChange}
      onMessagesChange={setChatMessages}
      onPersistLocationPin={handlePersistLocationPin}
      onRequestFinishProject={handleFinishButtonClick}
      onShowStationOnMap={handleShowStationOnMap}
      onObjectiveConfirmed={() => {
        try {} catch (_) {}
        // setCelebrateGoals(true);
        // setShowGoalsModal(true);
      }}
      onOpenGoalsModal={() => {
        // Open goals modal without celebration for initial setup
        setCelebrateGoals(false);
        setShowGoalsModal(true);
      }}
      onSubmittedWithImage={() => {
        if (isMobile) {
          setChatPanelHeight(FULL_HEIGHT);
        }
      }}
      onInteractiveCardInteraction={(cardType) => {
        if (isMobile) {
          // Set flag to prevent focus management from collapsing the panel
          isInteractiveCardInteractionRef.current = true;
          
          // Use MEDIUM_HEIGHT for location cards, FULL_HEIGHT for others
          const targetHeight = cardType === 'location' ? MEDIUM_HEIGHT : FULL_HEIGHT;
          setChatPanelHeight(targetHeight);
          
          // Clear flag after a short delay to allow focus changes to settle
          setTimeout(() => {
            isInteractiveCardInteractionRef.current = false;
          }, 200);
        }
      }}
    />
  );

  if (isMobile) {
    // Mobile layout: stacked with draggable chat panel
    return (
      <div 
        ref={containerRef}
        className="mobile-container fixed inset-0 flex flex-col bg-white dark:bg-gray-900 overflow-hidden"
        style={{
          height: '100dvh', // Dynamic viewport height for modern browsers, fallback to 100vh via CSS
          touchAction: 'manipulation', // Allow native pinch-zoom on the map while keeping scroll snappy
          position: 'fixed', // Ensure it's truly fixed to viewport
          width: '100vw'
        }}
      >
        {/* Map Panel (background) */}
        {showSidebar && (
          <div ref={mapContainerRef} className="absolute inset-0 w-full h-full z-10 top-0">
            <SidebarSection 
              project={project} 
              aiSuggestions={[...persistedAiPins, ...aiSuggestions]} 
            messagePins={messagePins}
            showMessagePins={showMessagePins}
            bottomOverlayPercent={(chatPanelHeight || 0) / 100}
            mapZoomTarget={mapZoomTarget}
            liveTrailPoints={liveTrailPoints}
            peerLocations={peerLocations}
            photoPins={photoPins}
            currentObjectiveId={currentObjectiveId}
            stationFocusRequest={stationFocusRequest}
          />
            
            {/* Backdrop overlay for status bar when chat is expanded */}
            {hasMobileStatusBar && (
              <div 
                className="fixed top-0 left-0 right-0 w-full h-full bg-gray-900/70"
                style={{ 
                  zIndex: 49999,
                  position: 'fixed',
                  top: 0,
                  height: 'calc(100% + var(--app-safe-area-top))',
                  opacity: chatPanelHeight >= 85 ? 1 : 0,
                  pointerEvents: 'none', // Visual backdrop only; never block map interactions
                  transition: chatPanelHeight >= 85 
                    ? 'opacity 500ms ease-in-out' 
                    : 'opacity 150ms ease-out'
                }}
              />
            )}
            
            {/* Project Status Bar - Mobile (hidden in read-only mode - Exit button is in the read-only banner) */}
            {hasMobileStatusBar && (
              <div 
                className="fixed top-0 left-0 right-0 w-full"
                style={{ 
                  zIndex: 50000,
                  position: 'fixed',
                  top: 'var(--app-safe-area-top)'
                }}
              >
                <ProjectStatusBar
                  project={project}
                  onFinish={handleFinishButtonClick}
                  allowPointerPassthrough
                />
              </div>
            )}
          </div>
        )}

        {/* Chat Panel (draggable overlay) */}
        {showChat && (
          <div
            ref={chatPanelRef}
            className={`mobile-chat-panel fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 ${
              chatPanelHeight === FULL_HEIGHT ? 'rounded-t-[1.5rem]' : 'rounded-t-[1.5rem]'
            } ${
              isDragging ? 'transition-none' : 'transition-[height,transform] duration-300 ease-out'
            }`}
            style={{
              // When fully expanded, stop below the top action row and native safe area.
              height: chatPanelHeight === FULL_HEIGHT 
                ? fullHeightChatPanelCss
                : `${chatPanelHeight}%`,
              zIndex: 1010,
              position: 'fixed',
              bottom: '0',
              paddingBottom: 'env(safe-area-inset-bottom, 0)',
              maxHeight: fullHeightChatPanelCss,
              minHeight: '22vh'
            }}
          >
            {/* Drag Handle - Intuitive pull affordance */}
            <div
              ref={dragHandleRef}
              className="drag-handle w-full cursor-grab active:cursor-grabbing"
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              style={{
                touchAction: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                paddingTop: '16px',
                paddingBottom: '12px'
              }}
            >
              <div className="drag-indicator"></div>
            </div>
            
            {/* Chat Content */}
            <div className="h-full flex flex-col" style={{ paddingTop: '8px' }}>
              <div 
                className="mobile-chat-content flex-1 overflow-hidden"
                style={{
                  paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1.5rem)',
                  touchAction: 'pan-y', // Only allow vertical scrolling within chat
                  overscrollBehavior: 'contain' // Prevent scroll chaining
                }}
              >
                {chatSectionElement}
              </div>
            </div>
          </div>
        )}
        
        {/* Finish Confirmation Modal */}
        {showFinishConfirmation && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
            <div 
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-[340px] w-full shadow-2xl animate-[modal-appear_0.25s_ease-out]"
              style={{ 
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)' 
              }}
            >
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                {isSiteProject ? 'End Session?' : 'All done?'}
              </h3>
              
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
                  {isSiteProject 
                  ? 'Your session will be saved and archived.'
                  : 'Your progress will be saved for review.'
                  }
                </p>
              
              <div className="flex gap-3">
                <button
                  onClick={handleCancelFinish}
                  className="flex-1 px-4 py-2.5 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl font-medium transition-colors"
                >
                  {isSiteProject ? 'Continue' : 'Resume'}
                </button>
                <button
                  onClick={handleFinishProject}
                  disabled={finishProjectMutation.isLoading}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {finishProjectMutation.isLoading 
                    ? 'Ending...'
                    : (isSiteProject ? 'End' : 'Finish')}
                </button>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/60 text-center">
                <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 mb-2">
                  need a breather?
                </p>
                <button
                  type="button"
                  onClick={handleGoToProjectSettings}
                  className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Return to project settings</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Goals Modal - Mobile */}
        {showGoalsModal && (
          <GoalsModal
            isOpen={showGoalsModal}
            onClose={() => { setShowGoalsModal(false); setCelebrateGoals(false); }}
            project={project}
            celebrate={celebrateGoals}
            currentUser={user}
          />
        )}

        {/* Participant Join/Leave Notifications - Mobile */}
        {project?.status === 'ongoing' && (
          <ParticipantNotificationManager notifications={participantNotifications} />
        )}
      </div>
    );
  }

  // Desktop layout: side-by-side
  return (
    <div className="flex h-full bg-white dark:bg-gray-900 overflow-hidden">
      {/* Sidebar Panel */}
      {showSidebar && (
        <div className="w-[50%] flex-shrink-0 relative">
          <SidebarSection 
            project={project} 
            aiSuggestions={[...persistedAiPins, ...aiSuggestions]} 
            messagePins={messagePins}
            showMessagePins={showMessagePins}
            liveTrailPoints={liveTrailPoints}
            mapZoomTarget={mapZoomTarget}
            peerLocations={peerLocations}
            photoPins={photoPins}
            currentObjectiveId={currentObjectiveId}
            stationFocusRequest={stationFocusRequest}
          />
          
          {/* Project Status Bar - Desktop (hidden in read-only mode - Exit button is in the read-only banner) */}
          {!isChatReadOnly && (
            <div 
              className="absolute top-4 md:top-1.5 left-2 right-2"
              style={{ zIndex: 1000 }}
            >
              {project && (project.status !== 'finished' || isSiteProject) && (
                <ProjectStatusBar
                  project={project}
                  onFinish={handleFinishButtonClick}
                  allowPointerPassthrough
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Chat Panel */}
      {showChat && (
        <div className="flex-1 min-w-0 relative h-full flex flex-col">
          <div className="flex-1 overflow-hidden">
            {chatSectionElement}
      {/* Goals Modal trigger on confirm */}
      {showGoalsModal && (
        <GoalsModal
          isOpen={showGoalsModal}
          onClose={() => { setShowGoalsModal(false); setCelebrateGoals(false); }}
          project={project}
          celebrate={celebrateGoals}
          currentUser={user}
        />
      )}
          </div>
        </div>
      )}
      
      {/* Finish Confirmation Modal */}
      {showFinishConfirmation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
          <div 
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-[340px] w-full shadow-2xl animate-[modal-appear_0.25s_ease-out]"
            style={{ 
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)' 
            }}
          >
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
              {isSiteProject ? 'End Session?' : 'All done?'}
            </h3>
            
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
                {isSiteProject 
                ? 'Your session will be saved and archived.'
                : 'Your progress will be saved for review.'
                }
              </p>
            
            <div className="flex gap-3">
              <button
                onClick={handleCancelFinish}
                className="flex-1 px-4 py-2.5 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl font-medium transition-colors"
              >
                {isSiteProject ? 'Continue' : 'Resume'}
              </button>
              <button
                onClick={handleFinishProject}
                disabled={finishProjectMutation.isLoading}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {finishProjectMutation.isLoading 
                  ? 'Ending...'
                  : (isSiteProject ? 'End' : 'Finish')}
              </button>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/60 text-center">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 mb-2">
                need a breather?
              </p>
              <button
                type="button"
                onClick={handleGoToProjectSettings}
                className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Return to project settings</span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Participant Join/Leave Notifications */}
      {project?.status === 'ongoing' && (
        <ParticipantNotificationManager notifications={participantNotifications} />
      )}
    </div>
  );
};

export default ChatPage;
